import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { checkRateLimit, detectAnomalies } from '@/adapters/security';

export function useUserDevices(userId?: string) {
  return useQuery({
    queryKey: ['user-devices', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('user_devices')
        .select('*')
        .eq('user_id', userId)
        .order('last_seen_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useUserSessions(userId?: string) {
  return useQuery({
    queryKey: ['user-sessions', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*, user_devices(*)')
        .eq('user_id', userId)
        .order('started_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useSecurityEvents(userId?: string) {
  return useQuery({
    queryKey: ['security-events', userId],
    queryFn: async () => {
      let query = supabase
        .from('security_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (userId) {
        query = query.eq('user_id', userId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function useRateLimit() {
  return useMutation({
    mutationFn: async ({
      identifier,
      identifierType,
      endpoint,
      maxRequests = 100,
      windowMinutes = 60,
    }: {
      identifier: string;
      identifierType: 'user' | 'ip' | 'device';
      endpoint: string;
      maxRequests?: number;
      windowMinutes?: number;
    }) => {
      return checkRateLimit(identifier, identifierType, endpoint, maxRequests, windowMinutes);
    },
  });
}

export function useDetectAnomalies(userId?: string) {
  return useQuery({
    queryKey: ['anomalies', userId],
    queryFn: async () => {
      if (!userId) return [];
      return detectAnomalies(userId);
    },
    enabled: !!userId,
    refetchInterval: 300000, // Check every 5 minutes
  });
}

export function useBlockDevice() {
  return useMutation({
    mutationFn: async (deviceId: string) => {
      const { error } = await supabase
        .from('user_devices')
        .update({ is_blocked: true })
        .eq('id', deviceId);
      
      if (error) throw error;
    },
  });
}

export function useTerminateSession() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from('user_sessions')
        .update({ is_active: false })
        .eq('id', sessionId);
      
      if (error) throw error;
    },
  });
}
